/*
============================================================================
SOGNO CUSTOM — HEADER JAVASCRIPT
File: assets/sogno-header.js
Project: Sogno87
Base theme: Sleek Spirit 2.4.0

LOGO ANIMATION V4
- Hero logo moves naturally toward top-left
- Vertical movement comes from normal page scroll
- Horizontal movement + scale use transform only
- No floating clone
- No layout-height changes
- No scrollTo
- No scrollIntoView
- No reload
============================================================================
*/

(() => {
  'use strict';


  /* ==========================================================================
     CONSTANTS
     ========================================================================== */

  const HEADER_SELECTOR =
    '[data-sogno-header]';

  const HERO_LOGO_SELECTOR =
    '[data-sogno-hero-logo]';

  const HEADER_LOGO_SELECTOR =
    '.sogno-header__logo--desktop, .header__logo--desktop';

  const HEADER_MAIN_SELECTOR =
    '.sogno-header__main';

  const DESKTOP_QUERY =
    '(min-width: 1024px)';

  const REDUCED_MOTION_QUERY =
    '(prefers-reduced-motion: reduce)';

  const DRAWER_IDS = [
    'MenuDrawer',
    'SearchDrawer',
    'CartDrawer'
  ];


  /* ==========================================================================
     HELPERS
     ========================================================================== */

  const clamp = (
    value,
    min,
    max
  ) => {
    return Math.min(
      Math.max(
        value,
        min
      ),
      max
    );
  };


  const lerp = (
    start,
    end,
    progress
  ) => {
    return (
      start +
      (
        end - start
      ) *
      progress
    );
  };


  const smoothstep = (
    value
  ) => {
    const t =
      clamp(
        value,
        0,
        1
      );

    return (
      t *
      t *
      (
        3 -
        2 * t
      )
    );
  };


  const setTextIfNeeded = (
    element,
    text
  ) => {
    if (!element) {
      return;
    }

    if (
      element.textContent.trim() !==
      text
    ) {
      element.textContent =
        text;
    }
  };


  const removeLegacyFloatingLogos =
    () => {
      document
        .querySelectorAll(
          '.sogno-floating-logo'
        )
        .forEach(
          (logo) => {
            logo.remove();
          }
        );
    };


  /* ==========================================================================
     SOGNO HEADER CONTROLLER
     ========================================================================== */

  class SognoHeaderController {

    constructor(header) {

      this.header =
        header;

      this.isHome =
        header.dataset.home ===
        'true';

      this.animationEnabled =
        header.dataset.logoAnimation ===
        'true';

      this.desktopMedia =
        window.matchMedia(
          DESKTOP_QUERY
        );

      this.reducedMotionMedia =
        window.matchMedia(
          REDUCED_MOTION_QUERY
        );


      this.heroLogo =
        null;

      this.destinationLogo =
        null;

      this.mainHeader =
        null;


      /*
      Geometry cached outside scroll loop.
      */

      this.sourceCenterX =
        0;

      this.sourceCenterDocumentY =
        0;

      this.sourceWidth =
        0;

      this.destinationCenterX =
        0;

      this.destinationCenterY =
        0;

      this.destinationWidth =
        0;

      this.deltaX =
        0;

      this.endScale =
        1;

      this.animationEndScroll =
        1;


      /*
      Animation state.
      */

      this.animationReady =
        false;

      this.ticking =
        false;

      this.resizeTimer =
        null;

      this.arrived =
        false;


      /*
      Original inline styles.
      */

      this.heroStyles =
        null;

      this.destinationStyles =
        null;


      /*
      Bind.
      */

      this.onScroll =
        this.onScroll.bind(
          this
        );

      this.onResize =
        this.onResize.bind(
          this
        );

      this.onMediaChange =
        this.onMediaChange.bind(
          this
        );

      this.onPageShow =
        this.onPageShow.bind(
          this
        );


      this.init();

    }


    /* ========================================================================
       INIT
       ======================================================================== */

    init() {

      removeLegacyFloatingLogos();


      window.addEventListener(
        'scroll',
        this.onScroll,
        {
          passive: true
        }
      );


      window.addEventListener(
        'resize',
        this.onResize,
        {
          passive: true
        }
      );


      window.addEventListener(
        'pageshow',
        this.onPageShow
      );


      if (
        this.desktopMedia
          .addEventListener
      ) {

        this.desktopMedia
          .addEventListener(
            'change',
            this.onMediaChange
          );


        this.reducedMotionMedia
          .addEventListener(
            'change',
            this.onMediaChange
          );

      }


      requestAnimationFrame(
        () => {

          requestAnimationFrame(
            () => {

              this.configure();

            }
          );

        }
      );


      /*
      Fonts may slightly alter dimensions.
      Recalculate once they are ready.
      */

      if (
        document.fonts &&
        document.fonts.ready
      ) {

        document.fonts.ready
          .then(
            () => {

              if (
                this.animationReady
              ) {

                this.measure();

              }

            }
          )
          .catch(
            () => {}
          );

      }

    }


    /* ========================================================================
       CAN ANIMATE
       ======================================================================== */

    canAnimate() {

      return (
        this.isHome &&
        this.animationEnabled &&
        this.desktopMedia.matches &&
        !this.reducedMotionMedia.matches
      );

    }


    /* ========================================================================
       CONFIGURE
       ======================================================================== */

    configure() {

      removeLegacyFloatingLogos();


      this.heroLogo =
        document.querySelector(
          HERO_LOGO_SELECTOR
        );


      this.destinationLogo =
        this.header.querySelector(
          HEADER_LOGO_SELECTOR
        );


      this.mainHeader =
        this.header.querySelector(
          HEADER_MAIN_SELECTOR
        ) ||
        this.header;


      if (
        !this.canAnimate() ||
        !this.heroLogo ||
        !this.destinationLogo
      ) {

        this.disableAnimation();

        return;

      }


      this.storeStyles();

      this.prepareElements();


      /*
      Wait for hero logo image if necessary.
      */

      if (
        this.heroLogo.tagName ===
        'IMG' &&
        !this.heroLogo.complete
      ) {

        this.heroLogo.addEventListener(
          'load',
          () => {

            this.measure();

          },
          {
            once: true
          }
        );

      }


      this.measure();

    }


    /* ========================================================================
       STORE ORIGINAL STYLES
       ======================================================================== */

    storeStyles() {

      if (
        !this.heroStyles
      ) {

        this.heroStyles = {

          transform:
            this.heroLogo.style.transform,

          transformOrigin:
            this.heroLogo.style.transformOrigin,

          transition:
            this.heroLogo.style.transition,

          opacity:
            this.heroLogo.style.opacity,

          visibility:
            this.heroLogo.style.visibility,

          willChange:
            this.heroLogo.style.willChange

        };

      }


      if (
        !this.destinationStyles
      ) {

        this.destinationStyles = {

          transform:
            this.destinationLogo.style.transform,

          transition:
            this.destinationLogo.style.transition,

          opacity:
            this.destinationLogo.style.opacity,

          visibility:
            this.destinationLogo.style.visibility,

          pointerEvents:
            this.destinationLogo.style.pointerEvents,

          willChange:
            this.destinationLogo.style.willChange

        };

      }

    }


    /* ========================================================================
       PREPARE
       ======================================================================== */

    prepareElements() {

      /*
      Hero logo.

      IMPORTANT:
      transform does not change document geometry.
      */

      this.heroLogo.style.transformOrigin =
        'center center';

      this.heroLogo.style.transition =
        'none';

      this.heroLogo.style.willChange =
        'transform, opacity';

      this.heroLogo.style.visibility =
        'visible';


      /*
      Real header logo.
      */

      this.destinationLogo.style.transition =
        'none';

      this.destinationLogo.style.willChange =
        'opacity, transform';

      this.destinationLogo.style.visibility =
        'visible';

      this.destinationLogo.style.opacity =
        '0';

      this.destinationLogo.style.transform =
        'scale(.96)';

      this.destinationLogo.style.pointerEvents =
        'none';


      this.header.classList.remove(
        'sogno-header--logo-arrived'
      );

    }


    /* ========================================================================
       MEASURE

       Runs only:
       - initialization
       - resize
       - fonts ready
       - pageshow

       NEVER while actively scrolling.
       ======================================================================== */

    measure() {

      if (
        !this.canAnimate() ||
        !this.heroLogo ||
        !this.destinationLogo ||
        !this.mainHeader
      ) {

        return;

      }


      const heroRect =
        this.heroLogo
          .getBoundingClientRect();


      const destinationRect =
        this.destinationLogo
          .getBoundingClientRect();


      const mainHeaderRect =
        this.mainHeader
          .getBoundingClientRect();


      if (
        heroRect.width <= 0 ||
        heroRect.height <= 0 ||
        destinationRect.width <= 0 ||
        mainHeaderRect.height <= 0
      ) {

        return;

      }


      /*
      HERO CENTER X
      viewport coordinate at initial position.
      */

      this.sourceCenterX =
        heroRect.left +
        (
          heroRect.width /
          2
        );


      /*
      HERO CENTER Y
      converted to document coordinate.

      This stays constant even while page scrolls.
      */

      this.sourceCenterDocumentY =
        heroRect.top +
        window.scrollY +
        (
          heroRect.height /
          2
        );


      this.sourceWidth =
        heroRect.width;


      /*
      HEADER DESTINATION.

      Horizontal position is taken from actual
      desktop header logo.

      Vertical destination is the center of the
      sticky main header.
      */

      this.destinationCenterX =
        destinationRect.left +
        (
          destinationRect.width /
          2
        );


      this.destinationCenterY =
        mainHeaderRect.height /
        2;


      this.destinationWidth =
        destinationRect.width;


      /*
      Horizontal movement only.

      Vertical movement is produced naturally
      by the browser's normal page scroll.
      */

      this.deltaX =
        this.destinationCenterX -
        this.sourceCenterX;


      this.endScale =
        clamp(
          this.destinationWidth /
          this.sourceWidth,
          0.08,
          1
        );


      /*
      The animation finishes exactly when the
      naturally scrolling hero logo reaches
      the vertical center of the sticky header.

      sourceCenterDocumentY - scrollY
      =
      destinationCenterY

      Therefore:
      scrollY =
      sourceCenterDocumentY - destinationCenterY
      */

      this.animationEndScroll =
        Math.max(
          1,
          this.sourceCenterDocumentY -
          this.destinationCenterY
        );


      this.animationReady =
        true;


      this.updateAnimation();

    }


    /* ========================================================================
       SCROLL

       No layout reads here.
       Only schedules one visual frame.
       ======================================================================== */

    onScroll() {

      if (
        !this.animationReady ||
        this.ticking
      ) {

        return;

      }


      this.ticking =
        true;


      requestAnimationFrame(
        () => {

          this.updateAnimation();

          this.ticking =
            false;

        }
      );

    }


    /* ========================================================================
       UPDATE ANIMATION
       ======================================================================== */

    updateAnimation() {

      if (
        !this.animationReady ||
        !this.heroLogo ||
        !this.destinationLogo
      ) {

        return;

      }


      const scrollY =
        Math.max(
          window.scrollY,
          0
        );


      /*
      0 → hero center
      1 → header destination
      */

      const progress =
        clamp(
          scrollY /
          this.animationEndScroll,
          0,
          1
        );


      /*
      Smooth horizontal movement and scale.

      Y remains controlled exclusively by normal scroll.
      */

      const motion =
        smoothstep(
          progress
        );


      const translateX =
        this.deltaX *
        motion;


      const scale =
        lerp(
          1,
          this.endScale,
          motion
        );


      /*
      HERO LOGO

      It moves LEFT and gets smaller.
      It moves UP naturally because the page scrolls.
      */

      this.heroLogo.style.transform =
        `translate3d(${translateX}px, 0, 0) scale(${scale})`;


      /*
      Crossfade only near the destination.
      */

      const heroFade =
        clamp(
          (
            progress -
            0.84
          ) /
          0.16,
          0,
          1
        );


      const headerFade =
        clamp(
          (
            progress -
            0.88
          ) /
          0.12,
          0,
          1
        );


      this.heroLogo.style.opacity =
        String(
          1 -
          heroFade
        );


      this.destinationLogo.style.opacity =
        String(
          headerFade
        );


      this.destinationLogo.style.transform =
        `scale(${lerp(
          0.96,
          1,
          headerFade
        )})`;


      /*
      Only switch pointer/visibility state once.
      No class thrashing during scroll.
      */

      const hasArrived =
        progress >=
        0.999;


      if (
        hasArrived !==
        this.arrived
      ) {

        this.arrived =
          hasArrived;


        this.header.classList.toggle(
          'sogno-header--logo-arrived',
          hasArrived
        );


        this.destinationLogo.style.pointerEvents =
          hasArrived
            ? 'auto'
            : 'none';


        this.heroLogo.style.visibility =
          hasArrived
            ? 'hidden'
            : 'visible';

      }


      /*
      When returning upward, make sure hero is
      visible before opacity increases again.
      */

      if (
        !hasArrived &&
        this.heroLogo.style.visibility !==
        'visible'
      ) {

        this.heroLogo.style.visibility =
          'visible';

      }

    }


    /* ========================================================================
       RESIZE
       ======================================================================== */

    onResize() {

      window.clearTimeout(
        this.resizeTimer
      );


      this.resizeTimer =
        window.setTimeout(
          () => {

            if (
              !this.canAnimate()
            ) {

              this.disableAnimation();

              return;

            }


            this.measure();

          },
          160
        );

    }


    /* ========================================================================
       MEDIA CHANGE
       ======================================================================== */

    onMediaChange() {

      if (
        this.canAnimate()
      ) {

        this.configure();

      } else {

        this.disableAnimation();

      }

    }


    /* ========================================================================
       PAGESHOW
       ======================================================================== */

    onPageShow() {

      if (
        !this.canAnimate()
      ) {

        return;

      }


      requestAnimationFrame(
        () => {

          this.measure();

        }
      );

    }


    /* ========================================================================
       DISABLE
       ======================================================================== */

    disableAnimation() {

      this.animationReady =
        false;

      this.arrived =
        false;


      removeLegacyFloatingLogos();


      if (
        this.heroLogo
      ) {

        if (
          this.heroStyles
        ) {

          this.heroLogo.style.transform =
            this.heroStyles.transform;

          this.heroLogo.style.transformOrigin =
            this.heroStyles.transformOrigin;

          this.heroLogo.style.transition =
            this.heroStyles.transition;

          this.heroLogo.style.opacity =
            this.heroStyles.opacity;

          this.heroLogo.style.visibility =
            this.heroStyles.visibility;

          this.heroLogo.style.willChange =
            this.heroStyles.willChange;

        } else {

          this.heroLogo.style.transform =
            '';

          this.heroLogo.style.opacity =
            '';

          this.heroLogo.style.visibility =
            '';

        }

      }


      if (
        this.destinationLogo
      ) {

        if (
          this.destinationStyles
        ) {

          this.destinationLogo.style.transform =
            this.destinationStyles.transform;

          this.destinationLogo.style.transition =
            this.destinationStyles.transition;

          this.destinationLogo.style.opacity =
            this.destinationStyles.opacity;

          this.destinationLogo.style.visibility =
            this.destinationStyles.visibility;

          this.destinationLogo.style.pointerEvents =
            this.destinationStyles.pointerEvents;

          this.destinationLogo.style.willChange =
            this.destinationStyles.willChange;

        } else {

          this.destinationLogo.style.transform =
            '';

          this.destinationLogo.style.transition =
            '';

          this.destinationLogo.style.opacity =
            '';

          this.destinationLogo.style.visibility =
            '';

          this.destinationLogo.style.pointerEvents =
            '';

        }

      }


      /*
      Normal header logo on mobile,
      internal pages or disabled animation.
      */

      this.header.classList.add(
        'sogno-header--logo-arrived'
      );

    }


    /* ========================================================================
       REBUILD
       ======================================================================== */

    rebuild() {

      this.animationReady =
        false;


      removeLegacyFloatingLogos();


      this.heroLogo =
        null;

      this.destinationLogo =
        null;

      this.mainHeader =
        null;


      this.heroStyles =
        null;

      this.destinationStyles =
        null;


      requestAnimationFrame(
        () => {

          requestAnimationFrame(
            () => {

              this.configure();

            }
          );

        }
      );

    }


    /* ========================================================================
       DESTROY
       ======================================================================== */

    destroy() {

      window.removeEventListener(
        'scroll',
        this.onScroll
      );


      window.removeEventListener(
        'resize',
        this.onResize
      );


      window.removeEventListener(
        'pageshow',
        this.onPageShow
      );


      if (
        this.desktopMedia
          .removeEventListener
      ) {

        this.desktopMedia
          .removeEventListener(
            'change',
            this.onMediaChange
          );


        this.reducedMotionMedia
          .removeEventListener(
            'change',
            this.onMediaChange
          );

      }


      window.clearTimeout(
        this.resizeTimer
      );


      this.disableAnimation();


      this.header.sognoController =
        null;

    }

  }


  /* ==========================================================================
     DRAWERS
     ========================================================================== */

  const hideDrawer = (
    drawer
  ) => {

    if (!drawer) {
      return;
    }


    if (
      typeof drawer.hide ===
      'function'
    ) {

      try {

        drawer.hide();

      } catch (error) {

        /*
        Preserve Sleek native behavior.
        */

      }

    }

  };


  const closeOtherDrawers = (
    activeId
  ) => {

    DRAWER_IDS.forEach(
      (drawerId) => {

        if (
          drawerId ===
          activeId
        ) {

          return;

        }


        hideDrawer(
          document.getElementById(
            drawerId
          )
        );

      }
    );

  };


  document.addEventListener(
    'click',
    (event) => {

      const trigger =
        event.target.closest(
          [
            '[aria-controls="MenuDrawer"]',
            '[aria-controls="SearchDrawer"]',
            '[aria-controls="CartDrawer"]'
          ].join(',')
        );


      if (!trigger) {
        return;
      }


      const activeId =
        trigger.getAttribute(
          'aria-controls'
        );


      if (!activeId) {
        return;
      }


      closeOtherDrawers(
        activeId
      );

    },
    true
  );


  /* ==========================================================================
     CART COPY
     ========================================================================== */

  const applySognoCartCopy =
    () => {

      const cartDrawer =
        document.getElementById(
          'CartDrawer'
        );


      if (!cartDrawer) {
        return;
      }


      const cartTitle =
        cartDrawer.querySelector(
          '.cart-drawer__header .drawer__heading span'
        );


      setTextIfNeeded(
        cartTitle,
        'Tu Carrito'
      );


      const emptyTitle =
        cartDrawer.querySelector(
          '.cart-drawer__empty .h4'
        );


      setTextIfNeeded(
        emptyTitle,
        'Tu Carrito está vacío'
      );


      const emptyMessage =
        cartDrawer.querySelector(
          '.cart-drawer__empty .rte'
        );


      setTextIfNeeded(
        emptyMessage,
        'Aún no sabes qué elegir, prueba buscando en estas colecciones recomendadas.'
      );


      const shippingGoal =
        cartDrawer.querySelector(
          'free-shipping-goal'
        );


      if (!shippingGoal) {
        return;
      }


      const defaultLabel =
        shippingGoal.querySelector(
          '.free-shipping-goal__label--default'
        );


      const successLabel =
        shippingGoal.querySelector(
          '.free-shipping-goal__label--success'
        );


      if (
        defaultLabel &&
        defaultLabel.dataset.sognoCopyReady !==
        'true'
      ) {

        const amountNode =
          defaultLabel.querySelector(
            '[data-left-to-spend]'
          );


        const amount =
          amountNode
            ? amountNode.innerHTML
            : '';


        defaultLabel.innerHTML =
          `Agrega <span data-left-to-spend>${amount}</span> más al carrito para que tu envío sea gratis.`;


        defaultLabel.dataset.sognoCopyReady =
          'true';

      }


      if (
        successLabel &&
        successLabel.dataset.sognoCopyReady !==
        'true'
      ) {

        successLabel.textContent =
          '¡Felicitaciones! Tienes tu envío gratis.';


        successLabel.dataset.sognoCopyReady =
          'true';

      }

    };


  const initCartCopyObserver =
    () => {

      const cartDrawer =
        document.getElementById(
          'CartDrawer'
        );


      if (!cartDrawer) {
        return;
      }


      applySognoCartCopy();


      if (
        cartDrawer.sognoCopyObserver
      ) {

        cartDrawer
          .sognoCopyObserver
          .disconnect();

      }


      const observer =
        new MutationObserver(
          () => {

            applySognoCartCopy();

          }
        );


      observer.observe(
        cartDrawer,
        {

          subtree:
            true,

          childList:
            true

        }
      );


      cartDrawer.sognoCopyObserver =
        observer;

    };


  document.addEventListener(
    'cart:updated',
    () => {

      requestAnimationFrame(
        applySognoCartCopy
      );

    }
  );


  document.addEventListener(
    'cart:refresh',
    () => {

      requestAnimationFrame(
        applySognoCartCopy
      );

    }
  );


  /* ==========================================================================
     SEARCH — CLEAR -> BORRAR
     ========================================================================== */

  const translateSearchClear =
    () => {

      const searchDrawer =
        document.getElementById(
          'SearchDrawer'
        );


      if (!searchDrawer) {
        return;
      }


      const candidates =
        searchDrawer.querySelectorAll(
          'button, span, a'
        );


      candidates.forEach(
        (element) => {

          if (
            element.children.length >
            0
          ) {

            return;

          }


          const text =
            element.textContent
              ?.trim();


          if (
            text === 'Claro' ||
            text === 'Clear'
          ) {

            element.textContent =
              'Borrar';

          }

        }
      );

    };


  const initSearchTranslation =
    () => {

      const searchDrawer =
        document.getElementById(
          'SearchDrawer'
        );


      if (!searchDrawer) {
        return;
      }


      translateSearchClear();


      if (
        searchDrawer
          .sognoTranslationObserver
      ) {

        searchDrawer
          .sognoTranslationObserver
          .disconnect();

      }


      const observer =
        new MutationObserver(
          translateSearchClear
        );


      observer.observe(
        searchDrawer,
        {

          subtree:
            true,

          childList:
            true,

          characterData:
            true

        }
      );


      searchDrawer
        .sognoTranslationObserver =
        observer;

    };


  /* ==========================================================================
     MOBILE TOUCH FOCUS
     ========================================================================== */

  document.addEventListener(
    'pointerup',
    (event) => {

      if (
        event.pointerType !==
        'touch'
      ) {

        return;

      }


      const control =
        event.target.closest(
          [
            '.sogno-header__icon-button',
            '[aria-controls="MenuDrawer"]',
            '[aria-controls="SearchDrawer"]',
            '[aria-controls="CartDrawer"]',
            '#MenuDrawer button',
            '#SearchDrawer button',
            '#CartDrawer button',
            '#MenuDrawer a',
            '#SearchDrawer a',
            '#CartDrawer a'
          ].join(',')
        );


      if (!control) {
        return;
      }


      window.setTimeout(
        () => {

          if (
            typeof control.blur ===
            'function'
          ) {

            control.blur();

          }

        },
        80
      );

    }
  );


  /* ==========================================================================
     INITIALIZATION
     ========================================================================== */

  const initSognoHeaders =
    () => {

      document
        .querySelectorAll(
          HEADER_SELECTOR
        )
        .forEach(
          (header) => {

            if (
              header.sognoController
            ) {

              return;

            }


            header.sognoController =
              new SognoHeaderController(
                header
              );

          }
        );

    };


  const rebuildSognoHeaders =
    () => {

      document
        .querySelectorAll(
          HEADER_SELECTOR
        )
        .forEach(
          (header) => {

            if (
              header.sognoController
            ) {

              header
                .sognoController
                .rebuild();

            } else {

              header.sognoController =
                new SognoHeaderController(
                  header
                );

            }

          }
        );

    };


  const init =
    () => {

      removeLegacyFloatingLogos();

      initSognoHeaders();

      initSearchTranslation();

      initCartCopyObserver();

    };


  if (
    document.readyState ===
    'loading'
  ) {

    document.addEventListener(
      'DOMContentLoaded',
      init,
      {
        once: true
      }
    );

  } else {

    init();

  }


  /* ==========================================================================
     SHOPIFY THEME EDITOR
     ========================================================================== */

  if (
    window.Shopify &&
    Shopify.designMode
  ) {

    document.addEventListener(
      'shopify:section:load',
      () => {

        requestAnimationFrame(
          () => {

            requestAnimationFrame(
              () => {

                removeLegacyFloatingLogos();

                rebuildSognoHeaders();

                initSearchTranslation();

                initCartCopyObserver();

              }
            );

          }
        );

      }
    );


    document.addEventListener(
      'shopify:section:unload',
      (event) => {

        const header =
          event.target
            .querySelector?.(
              HEADER_SELECTOR
            );


        if (
          header &&
          header.sognoController
        ) {

          header
            .sognoController
            .destroy();

        }

      }
    );

  }

})();